import { take } from 'rxjs/operators';
import { subtitlesConfig } from './config/subtitles.config';
import { Component, OnDestroy, OnInit, forwardRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription, filter, tap } from 'rxjs';

import { FileValidatedInfo, UploaderValue } from './models';
import { SubtitlesErrorService, SubtitlesUploaderHttpsService, SubtitlesUploaderService, SubtitlesValidateService } from './services';
import { NgxFileDropEntry } from 'ngx-file-drop';

@Component({
    selector: 'app-subtitles-uploader',
    templateUrl: './subtitles-uploader.component.html',
    styleUrls: ['./subtitles-uploader.component.scss'],
    providers: [
        SubtitlesErrorService,
        SubtitlesUploaderHttpsService,
        SubtitlesUploaderService,
        SubtitlesValidateService,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SubtitlesUploaderComponent),
            multi: true,
        },
    ],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- legacy component, migrate to OnPush when touched
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SubtitlesUploaderComponent implements OnInit, OnDestroy, ControlValueAccessor {
    private subtitlesUploaderService = inject(SubtitlesUploaderService);
    private subtitlesValidateService = inject(SubtitlesValidateService);

    validatingFile$ = this.subtitlesValidateService.validatingFile$;
    validating$ = this.subtitlesValidateService.validating$;
    private _value!: UploaderValue;

    private readonly subscriptions$ = new Subscription();

    private get value() {
        return this._value;
    }

    private set value(value: UploaderValue) {
        this._value = value;
        this.propagateChange(value);
    }

    ngOnInit(): void {
        this.subtitlesUploaderService.init();

        this.subscriptions$.add(
            this.subtitlesValidateService.validatedInfo$
                .pipe(
                    filter((validatedInfo): validatedInfo is FileValidatedInfo => Boolean(validatedInfo)),
                    tap((validatedInfo) => this.setValidatedValue(validatedInfo)),
                )
                .subscribe(),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
        this.subtitlesUploaderService.destroy();
    }

    get acceptedFileType() {
        return subtitlesConfig.requirements.acceptedFileType;
    }

    propagateChange: (_: UploaderValue) => void = () => undefined;
    onTouched: () => void = () => undefined;

    registerOnChange(fn: (_: UploaderValue) => void): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    writeValue(value: UploaderValue): void {
        this.value = value;
    }

    dropped(files: NgxFileDropEntry[]) {
        this.validating$
            .pipe(
                take(1),
                filter((validating) => !validating),
                tap(() => {
                    this.subtitlesUploaderService.onDropped(files);
                }),
            )
            .subscribe();
    }

    private setValidatedValue({ hash, file }: FileValidatedInfo) {
        this.value = {
            ...this.value,
            hash,
            file,
        };
    }
}
